import React, { useState, useCallback, useEffect } from 'react';
import { tr } from '../../../i18n/i18n';
import {
  Paginator,
  PaginatorPageChangeEvent,
  PaginatorRowsPerPageDropdownOptions,
  PaginatorCurrentPageReportOptions,
} from 'primereact/paginator';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';

interface AppPaginatorProps {
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, rows: number) => void;
  rowsPerPageOptions?: number[];
  showPageSizeDropdown?: boolean;
  showQuickJump?: boolean;
  variant?: 'table' | 'grid'; // 'table' = card-style with shadow (default), 'grid' = minimal styling
}

interface PageOption {
  label: string;
  value: number;
}

// Hook to detect mobile viewport
const useIsMobile = (breakpoint = 768): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

const AppPaginator: React.FC<AppPaginatorProps> = ({
  currentPage,
  pageSize,
  total,
  onPageChange,
  rowsPerPageOptions = [5, 10, 20, 50],
  showPageSizeDropdown = true,
  showQuickJump = true,
  variant = 'table',
}) => {
  const { t } = useTranslation();
  const [jumpPage, setJumpPage] = useState<string>('');
  const isMobile = useIsMobile();

  // Calculate total pages
  const totalPages = Math.ceil(total / pageSize) || 1;

  // Handle PrimeReact paginator page change
  const handlePaginatorChange = useCallback(
    (event: PaginatorPageChangeEvent) => {
      onPageChange(event.page, event.rows);
    },
    [onPageChange]
  );

  // Handle page size change from dropdown
  const handlePageSizeChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newPageSize = e.value as number;
      onPageChange(0, newPageSize); // Reset to first page
    },
    [onPageChange]
  );

  // Handle quick jump to page
  const handleJumpToPage = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const pageNum = parseInt(jumpPage, 10);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
          onPageChange(pageNum - 1, pageSize);
          setJumpPage('');
        }
      }
    },
    [jumpPage, totalPages, pageSize, onPageChange]
  );

  // Handle input change for jump page
  const handleJumpInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setJumpPage(value);
    }
  }, []);

  // Page size dropdown options
  const pageSizeOptions: PageOption[] = rowsPerPageOptions.map((size) => ({
    label: t('common.per_page', '{{count}} per page', { count: size }),
    value: size,
  }));

  // Custom template for rows per page dropdown - hidden on mobile
  const rowsPerPageDropdownTemplate = useCallback(
    (options: PaginatorRowsPerPageDropdownOptions) => {
      // Hide on mobile for cleaner UI
      if (!showPageSizeDropdown || isMobile) return null;

      return (
        <div className="app-paginator-rows-control">
          <span className="app-paginator-rows-label">{t('common.show', 'Show')}</span>
          <Dropdown
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
            optionLabel="label"
            optionValue="value"
            className="app-paginator-dropdown"
            appendTo={document.body}
            scrollHeight="250px"
          />
        </div>
      );
    },
    [showPageSizeDropdown, pageSize, pageSizeOptions, handlePageSizeChange, isMobile]
  );

  // Custom template for current page report with quick jump
  const currentPageReportTemplate = useCallback(
    (options: PaginatorCurrentPageReportOptions) => {
      const first = options.first;
      const last = options.last;
      const totalRecords = options.totalRecords;

      // Mobile: Show compact version - only page count
      if (isMobile) {
        return (
          <div className="app-paginator-info-wrapper app-paginator-mobile">
            <span className="app-paginator-report">
              {t('common.page', 'Page')}{' '}
              <span className="app-paginator-report-highlight">
                {currentPage + 1}
              </span>{' '}
              {t('common.of', 'of')} <span className="app-paginator-report-highlight">{totalPages}</span>
            </span>
          </div>
        );
      }

      return (
        <div className="app-paginator-info-wrapper">
          <span className="app-paginator-report">
            {t('common.showing', 'Showing')}{' '}
            <span className="app-paginator-report-highlight">
              {first}-{last}
            </span>{' '}
            {t('common.of', 'of')} <span className="app-paginator-report-highlight">{totalRecords}</span>
          </span>

          {showQuickJump && totalPages > 1 && (
            <div className="app-paginator-jump">
              <span className="app-paginator-jump-label">{t('common.go_to', 'Go to:')}</span>
              <InputText
                value={jumpPage}
                onChange={handleJumpInputChange}
                onKeyDown={handleJumpToPage}
                placeholder={`1-${totalPages}`}
                className="app-paginator-jump-input"
                aria-label={t('g.jump_to_page', 'Jump to page')}
              />
            </div>
          )}
        </div>
      );
    },
    [showQuickJump, totalPages, jumpPage, handleJumpInputChange, handleJumpToPage, isMobile, currentPage]
  );

  // Responsive layout: simpler on mobile
  const paginatorLayout = isMobile
    ? 'PrevPageLink PageLinks NextPageLink CurrentPageReport'
    : 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport';

  return (
    <div className={`app-paginator-container ${variant === 'grid' ? 'app-paginator-grid' : ''} ${isMobile ? 'app-paginator-mobile-view' : ''}`}>
      <Paginator
        first={currentPage * pageSize}
        rows={pageSize}
        totalRecords={total}
        onPageChange={handlePaginatorChange}
        template={{
          layout: paginatorLayout,
          RowsPerPageDropdown: rowsPerPageDropdownTemplate,
          CurrentPageReport: currentPageReportTemplate,
        }}
        className="app-paginator"
      />
    </div>
  );
};

export default AppPaginator;
